import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ClipboardList, AlertTriangle, ArrowRight, Loader2, MailOpen, Sparkles, CalendarDays, TrendingUp, Plug, MessageCircle, MessageSquare, CheckCircle2, Circle, X, Building2, Megaphone, Vote, Home, ShieldCheck, Users, Wrench, ArrowUp } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { AdvancedAnalytics } from "@/pages/AdvancedAnalytics";

const MetricCard = ({ icon: Icon, label, value, accent, testid, sub }) => (
  <div data-testid={testid} className="pop-in bg-card border border-border rounded-md p-6 hover:border-foreground/30 hover:-translate-y-0.5 transition-all duration-200">
    <div className="flex items-center justify-between mb-4">
      <span className="text-xs uppercase tracking-[0.18em] font-semibold text-muted-foreground">{label}</span>
      <Icon className={`h-5 w-5 ${accent}`} strokeWidth={2} />
    </div>
    <div className="font-head text-4xl font-extrabold tracking-tighter">{value}</div>
    {sub && <div className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1"><CalendarDays className="h-3 w-3" />{sub}</div>}
  </div>
);

const PRIORITY_BAR = { alta: "bg-red-500", media: "bg-amber-400", bassa: "bg-slate-300" };
const PRIORITY_LABEL = { alta: "Alta", media: "Media", bassa: "Bassa" };

const DistBar = ({ label, n, total, color }) => (
  <div className="flex items-center gap-4">
    <span className="text-xs font-medium w-20 text-muted-foreground">{label}</span>
    <div className="flex-1 h-2.5 bg-secondary rounded-full overflow-hidden">
      <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: total ? `${(n / total) * 100}%` : "0%" }} />
    </div>
    <span className="text-sm font-semibold w-8 text-right">{n}</span>
  </div>
);

const useCountUp = (target, duration = 900) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const t = Number(target) || 0;
    if (t <= 0) { setN(0); return; }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(t * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
};

const FeatureStat = ({ icon: Icon, label, value, sub, color, delta = 0, delay = 0, onClick, testid }) => {
  const n = useCountUp(value);
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{ animationDelay: `${delay}ms`, background: `linear-gradient(135deg, ${color}14, transparent 72%)` }}
      className="pop-in group relative text-left rounded-2xl border border-border p-5 flex flex-col gap-3 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:border-foreground/20 cursor-pointer">
      {delta > 0 && (
        <span
          data-testid={`${testid}-delta`}
          title="Nuovi negli ultimi 7 giorni"
          className="absolute top-3.5 right-3.5 inline-flex items-center gap-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-[11px] font-bold px-2 py-0.5 animate-in fade-in-0 zoom-in-75 duration-500"
          style={{ animationDelay: `${delay + 500}ms`, animationFillMode: "both" }}>
          <ArrowUp className="h-3 w-3" strokeWidth={2.5} />{delta}
        </span>
      )}
      <div className="h-11 w-11 rounded-xl flex items-center justify-center bg-card transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
        style={{ color, boxShadow: `0 0 0 1px ${color}33` }}>
        <Icon className="h-5 w-5" strokeWidth={2} />
      </div>
      <div className="font-head text-3xl font-extrabold tracking-tight tabular-nums leading-none">{n}</div>
      <div>
        <div className="text-sm font-semibold leading-tight">{label}</div>
        {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
      </div>
    </button>
  );
};

const RANGES = [[7, "7 giorni"], [30, "30 giorni"], [90, "90 giorni"]];

const OnbStep = ({ done, icon: Icon, title, desc, cta, onClick, testid }) => (
  <div data-testid={testid} className={`rounded-md border p-4 flex items-start gap-3 transition-colors ${done ? "border-emerald-500/30 bg-emerald-500/5" : "border-border bg-secondary/40"}`}>
    <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${done ? "bg-emerald-500 text-white" : "bg-[#007BFF] text-white"}`}>
      {done ? <CheckCircle2 className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
    </div>
    <div className="min-w-0 flex-1">
      <div className="font-semibold text-sm flex items-center gap-2">
        {title}
        {done && <span className="text-[10px] font-bold uppercase text-emerald-600">Fatto</span>}
      </div>
      <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{desc}</p>
      {!done && (
        <button onClick={onClick} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-[#007BFF] hover:underline">
          {cta} <ArrowRight className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
    {!done && <Circle className="h-4 w-4 text-muted-foreground/40 shrink-0" />}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [range, setRange] = useState(30);
  const [series, setSeries] = useState([]);
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [onb, setOnb] = useState(null); // { mailbox, condominio, studio }
  const [onbDismissed, setOnbDismissed] = useState(() => localStorage.getItem("onb_dismissed") === "1");
  const [smsWallet, setSmsWallet] = useState(null);
  const nav = useNavigate();

  const load = useCallback(() => api.get("/stats").then((r) => setStats(r.data)), []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/sms/wallet").then((r) => setSmsWallet(r.data)).catch(() => setSmsWallet(null));
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/accounts/summary").then((r) => (r.data.accounts || []).length > 0).catch(() => true),
      api.get("/buildings").then((r) => (r.data || []).length > 0).catch(() => true),
      api.get("/studio-profile").then((r) => !!r.data?.profile_complete).catch(() => true),
    ]).then(([mailbox, condominio, studio]) => setOnb({ mailbox, condominio, studio }));
  }, []);

  const dismissOnb = () => { localStorage.setItem("onb_dismissed", "1"); setOnbDismissed(true); };

  const loadSeries = useCallback(async () => {
    setSeriesLoading(true);
    try {
      const r = await api.get("/stats/timeseries", { params: { days: range } });
      setSeries(r.data.series || []);
    } catch (e) { console.error("Errore caricamento andamento:", e); }
    finally { setSeriesLoading(false); }
  }, [range]);
  useEffect(() => { loadSeries(); }, [loadSeries]);

  const fmtDay = (d) => {
    const dt = new Date(d + "T00:00:00");
    return dt.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });
  };

  if (!stats) return <div className="p-10 text-muted-foreground">Caricamento…</div>;

  return (
    <div className="fade-in">
      <header className="px-4 sm:px-10 pt-4 sm:pt-6 pb-4 border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-10">
        <div className="text-xs uppercase tracking-[0.2em] text-[#007BFF] font-semibold mb-1.5">Panoramica</div>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tighter">Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Automazione intelligente di email, PEC e documenti aziendali. Classificazione, estrazione dati e bozze di risposta generate dall'AI.
        </p>
      </header>

      <div className="p-4 sm:p-10 space-y-8">
        {onb && !onbDismissed && (!onb.mailbox || !onb.condominio || !onb.studio) && (
          <div data-testid="onboarding-banner" className="pop-in bg-card border border-[#007BFF]/30 rounded-md p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#007BFF] font-semibold mb-1">Configurazione iniziale</div>
                <h3 className="font-head text-xl font-bold tracking-tight">Completa la configurazione di Sedio</h3>
                <p className="text-sm text-muted-foreground mt-1">Bastano pochi passaggi per iniziare a ricevere ed elaborare le comunicazioni con l'AI.</p>
              </div>
              <button data-testid="onboarding-dismiss" onClick={dismissOnb} className="text-muted-foreground hover:text-foreground transition-colors shrink-0" title="Nascondi">
                <X className="h-4 w-4" />
              </button>
            </div>
            {(() => {
              const total = 3;
              const doneCount = [onb.condominio, onb.mailbox, onb.studio].filter(Boolean).length;
              return (
                <div className="mb-4" data-testid="onboarding-progress">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="font-semibold text-foreground">{doneCount} di {total} completati</span>
                    <span className="text-muted-foreground">{Math.round((doneCount / total) * 100)}%</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full overflow-hidden">
                    <div className="h-full bg-[#007BFF] rounded-full transition-all duration-500" style={{ width: `${(doneCount / total) * 100}%` }} />
                  </div>
                </div>
              );
            })()}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <OnbStep done={onb.studio} icon={ShieldCheck} testid="onboarding-studio"
                title="Completa lo Studio"
                desc="Registra ragione sociale, P.IVA e indirizzo dello Studio: sblocca tutte le funzioni e ferma il conto alla rovescia della prova."
                cta="Completa lo Studio" onClick={() => nav("/profilo")} />
              <OnbStep done={onb.mailbox} icon={Plug} testid="onboarding-mailbox"
                title="Collega una casella email"
                desc="Ricevi e analizza automaticamente le email dei condòmini."
                cta="Connetti Account" onClick={() => nav("/connetti")} />
              <OnbStep done={onb.condominio} icon={Building2} testid="onboarding-condominio"
                title="Crea il primo condominio"
                desc="Aggiungi anagrafica e condòmini per abbinare le comunicazioni."
                cta="Aggiungi condominio" onClick={() => nav("/condomini")} />
            </div>
          </div>
        )}

        {stats.total === 0 && (
          <div className="bg-[#0A0A0A] text-white rounded-md p-8">
            <h3 className="font-head text-2xl font-bold tracking-tight">Nessuna comunicazione ancora</h3>
            <p className="text-white/60 mt-1.5 max-w-xl">Collega una casella email in <span className="text-[#4DA3FF] font-medium">Connetti Account</span> per iniziare a ricevere ed elaborare automaticamente le comunicazioni con l'AI.</p>
          </div>
        )}

        <section>
          <div className="flex items-center gap-2 mb-5">
            <div className="text-xs uppercase tracking-[0.18em] text-[#007BFF] font-semibold flex items-center gap-2">
              <Sparkles className="h-4 w-4" />Gestione condominiale
            </div>
            <div className="h-px flex-1 bg-border" />
          </div>
          <div data-testid="feature-stats" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <FeatureStat testid="feat-condomini" icon={Building2} color="#007BFF" value={stats.condomini ?? 0} delta={(stats.delta7 || {}).condomini || 0} label="Condomìni" sub="gestiti" delay={0} onClick={() => nav("/condomini")} />
            <FeatureStat testid="feat-portali" icon={ShieldCheck} color="#8B5CF6" value={stats.portali ?? 0} delta={(stats.delta7 || {}).portali || 0} label="Portali condòmini" sub="attivi" delay={60} onClick={() => nav("/portali-condomini")} />
            <FeatureStat testid="feat-fornitori" icon={Wrench} color="#F59E0B" value={stats.fornitori ?? 0} delta={(stats.delta7 || {}).fornitori || 0} label="Fornitori" sub="in rubrica" delay={180} onClick={() => nav("/fornitori")} />
            <FeatureStat testid="feat-avvisi" icon={Megaphone} color="#EF4444" value={stats.avvisi ?? 0} delta={(stats.delta7 || {}).avvisi || 0} label="Avvisi in bacheca" sub="pubblicati" delay={240} onClick={() => nav("/condomini")} />
            <FeatureStat testid="feat-sondaggi" icon={Vote} color="#14B8A6" value={stats.sondaggi ?? 0} delta={(stats.delta7 || {}).sondaggi || 0} label="Sondaggi" sub={`${stats.sondaggi_aperti ?? 0} aperti al voto`} delay={300} onClick={() => nav("/condomini")} />
            <FeatureStat testid="feat-stime" icon={Home} color="#10B981" value={stats.stime_immobili ?? 0} delta={(stats.delta7 || {}).stime_immobili || 0} label="Stime immobili" sub="valutazioni AVM" delay={360} onClick={() => nav("/condomini")} />
            <FeatureStat testid="feat-pratiche-aperte" icon={ClipboardList} color="#EC4899" value={stats.pratiche_aperte ?? 0} delta={(stats.delta7 || {}).pratiche_aperte || 0} label="Pratiche aperte" sub="in lavorazione" delay={420} onClick={() => nav("/pratiche")} />
            <FeatureStat testid="feat-sms" icon={MessageSquare} color="#22C55E" value={stats.sms_total ?? 0} delta={(stats.delta7 || {}).sms || 0} label="SMS inviati" sub={`${stats.sms_delivered ?? 0} consegnati · ${stats.sms_pending ?? 0} in attesa`} delay={480} onClick={() => nav("/impostazioni")} />
          </div>
        </section>

        <div data-testid="timeseries-chart" className="bg-card border border-border rounded-md p-6">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
            <h3 className="font-head text-lg font-semibold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#007BFF]" />Andamento nel tempo</h3>
            <div className="flex items-center gap-1.5 bg-secondary rounded-md p-1">
              {RANGES.map(([d, lbl]) => (
                <button key={d} data-testid={`range-${d}`} onClick={() => setRange(d)}
                  className={`text-xs font-medium px-3 py-1.5 rounded transition-colors duration-150 ${range === d ? "bg-[#007BFF] text-white" : "text-muted-foreground hover:text-foreground"}`}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          {seriesLoading ? (
            <div className="h-[300px] flex items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gEmails" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#007BFF" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#007BFF" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gAuto" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#007BFF" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#007BFF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fontSize: 11, fill: "#94a3b8" }} interval="preserveStartEnd" minTickGap={24} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <Tooltip labelFormatter={fmtDay} contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="emails" name="Email ricevute" stroke="#007BFF" strokeWidth={2} fill="url(#gEmails)" />
                <Area type="monotone" dataKey="automations" name="Automazioni eseguite" stroke="#007BFF" strokeWidth={2} fill="url(#gAuto)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div data-testid="priority-distribution" className="lg:col-span-2 bg-card border border-border rounded-md p-6">
            <h3 className="font-head text-lg font-semibold mb-5 flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-500" />Distribuzione priorità comunicazioni</h3>
            <div className="space-y-3">
              {["alta", "media", "bassa"].map((p) => (
                <DistBar key={p} label={PRIORITY_LABEL[p]} n={(stats.by_priority || {})[p] || 0} total={stats.total} color={PRIORITY_BAR[p]} />
              ))}
            </div>
          </div>

          <div className="bg-[#0A0A0A] text-white rounded-md p-6 flex flex-col justify-between overflow-hidden relative">
            <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#007BFF]/20 blur-2xl" />
            <div className="relative">
              <MailOpen className="h-6 w-6 text-[#4DA3FF] mb-4" />
              <div className="text-xs uppercase tracking-[0.18em] text-white/40 font-semibold">Da leggere</div>
              <div className="font-head text-5xl font-extrabold tracking-tighter mt-2 tabular-nums">{stats.unread ?? 0}</div>
              <p className="text-white/50 text-sm mt-2">Comunicazioni non ancora lette. {stats.this_week ?? 0} arrivate negli ultimi 7 giorni.</p>
            </div>
            <Button data-testid="go-inbox-button" onClick={() => nav("/inbox")} variant="secondary" className="relative mt-6 rounded-md bg-white text-black hover:bg-white/90">
              Apri comunicazioni <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>

        {smsWallet && (() => {
          const bal = smsWallet.pool_balance ?? 0;
          const low = bal < 10;
          return (
            <button
              data-testid="sms-balance-bar"
              onClick={() => nav("/acquisti")}
              className={`w-full text-left rounded-xl border px-5 py-4 flex items-center justify-between gap-4 transition-all duration-200 hover:shadow-md cursor-pointer ${low ? "border-amber-500/60 bg-amber-500/10" : "border-border bg-card hover:border-foreground/20"}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${low ? "bg-amber-500/20" : "bg-[#22C55E]/15"}`}>
                  {low ? <AlertTriangle className="h-5 w-5 text-amber-500" /> : <MessageSquare className="h-5 w-5 text-[#22C55E]" />}
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.16em] font-semibold text-muted-foreground">Saldo pacchetti SMS</div>
                  <div className="flex items-baseline gap-2">
                    <span data-testid="sms-balance-bar-value" className={`font-head text-2xl font-extrabold tracking-tight ${low ? "text-amber-500" : ""}`}>{bal}</span>
                    <span className={`text-sm truncate ${low ? "text-amber-600 font-medium" : "text-muted-foreground"}`}>
                      {low ? "in esaurimento — ricarica ora" : "SMS disponibili"}
                    </span>
                  </div>
                </div>
              </div>
              <span className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold shrink-0 transition-colors ${low ? "bg-amber-500 text-white hover:bg-amber-600" : "bg-[#22C55E] text-white hover:bg-[#1DA84E]"}`}>
                Ricarica SMS <ArrowRight className="h-4 w-4" />
              </span>
            </button>
          );
        })()}

        <AdvancedAnalytics />
      </div>
    </div>
  );
}
